import { LoadingOverlay, useSnackbar, withDialog, type WithDialogProps } from '@kidslabs/mario-nextjs-react-ui-core';
import { type FC, useCallback, useEffect, useState, useTransition } from 'react';
import { cartItemReferenceStoreAPI, cartStoreAPI } from '../../stores';
import { useTranslation } from 'next-i18next';
import { syncState } from '@legendapp/state';
import { observer, useSelector } from '@legendapp/state/react';
import { formatPriceSimple } from '@kidslabs/module-catalog';
import { useCart, useCartItemReference, useCartItemsStockStatus, useCartActionsV3 } from '../../hooks';
import { useCartPriceSummary } from '../../hooks';
import { useForceCartSync } from '../../hooks/use-force-cart-sync';
import { useSession } from 'next-auth/react';
import { CartTotalView } from './cart-total-view';
import {
    type ConfigurableCartItem,
    type SimpleCartItem,
    useRecalculateCartTotalsMutation,
} from '../../types/front.graphql';
import { useRouter } from 'next/navigation';
import { useAuthenticationGuard } from '../../hooks';
import type { CartTotalProps } from './cart-total.types';

export const CartTotalInner: FC<CartTotalProps> = observer(
    ({ authenticationGuard, hideDialog, setDialogOptions, showDialog }: CartTotalProps) => {
        const state$ = syncState(cartItemReferenceStoreAPI.state.getStoreObservable());
        const syncedError = state$.error.get();
        const { cartId } = useCart();
        const { isOutOfStock } = useCartItemsStockStatus(cartId);
        const { t } = useTranslation('quote');
        const { showSnackbar } = useSnackbar();
        const [isLoading, setIsLoading] = useState(false);
        const { references$, getCartItemsHasInvalidLogics, removeInvalidSelectedLogics } = useCartItemReference();
        const router = useRouter();
        const { status } = useSession();
        const { handleSelectAllCartItemReference, isAllCartItemReferenceSelected } = useCartActionsV3();
        const [recalculateCartTotals] = useRecalculateCartTotalsMutation();
        const [isPending, startTransition] = useTransition();
        const { forceSync } = useForceCartSync();

        const isAuthenticated = status === 'authenticated';

        // Authentication guard — gate checkout navigation behind login
        const { requireAuthentication } = useAuthenticationGuard(
            authenticationGuard ?? { isAuthenticated: true, openLoginModal: () => {} }
        );

        // ✅ GET STOCK DATA FROM Legend-State store (matches mobile pattern)
        const stockAvailabilityMap = useSelector(() => cartStoreAPI.state.getAvailableItemsObservable().get());
        const references = references$().get() ?? [];

        // ✅ SELECTED ITEMS: Filter by isSelected AND in-stock (matches mobile pattern)
        const selectedItemIds = references
            .filter(ref => {
                const cartItem = ref.cartItem;
                const productSku = ref.parentProduct
                    ? (cartItem as ConfigurableCartItem)?.configured_variant?.sku
                    : (cartItem as SimpleCartItem)?.product?.sku;
                return ref.isSelected && !!cartItem && stockAvailabilityMap[productSku ?? '']?.isSalable;
            })
            .map(ref => ref.cartItem?.id)
            .filter(Boolean);

        // ✅ Shared price calculation logic (single source of truth with CartInformation)
        const { savingsAmount, grandTotalPrice, originalTotal, productOnlyGrandTotal } = useCartPriceSummary(t);

        // Format original price for display (only show when there are savings)
        const originalPrice = originalTotal > productOnlyGrandTotal ? formatPriceSimple(originalTotal) : undefined;

        const handleCloseDialog = useCallback(() => {
            hideDialog();
        }, [hideDialog]);

        const handleShowDialog = useCallback(
            (message: string, onConfirm?: () => void, onDismiss?: () => void) => {
                setDialogOptions({
                    title: t('text_notification', 'Thông báo'),
                    content: message,
                    acceptLabel: t('text_agree', 'Đồng ý'),
                    dismissLabel: t('text_cancel', 'Quay lại'),
                    loading: false,
                    onAccept: () => {
                        onConfirm?.();
                        hideDialog();
                    },
                    onDismiss: () => {
                        onDismiss?.();
                        hideDialog();
                    },
                });
                showDialog();
            },
            [setDialogOptions, showDialog, hideDialog, t]
        );

        const syncCartItemReferences = useCallback(async () => {
            // Authentication guard — if not authenticated, show login modal and return
            if (!requireAuthentication()) {
                return;
            }

            if (!selectedItemIds || selectedItemIds.length === 0) {
                showSnackbar({
                    message: t('text_no_items_selected', 'Vui lòng chọn sản phẩm để tiếp tục thanh toán'),
                    variant: 'warning',
                    duration: 3000,
                });
                return;
            }
            try {
                setIsLoading(true);

                // Force push local changes to server, then fetch back
                // server-authoritative state via fetchCartSyncReferences.
                // This ensures local state matches server before checkout.
                await forceSync();

                // Silently recalculate totals on server after sync
                // This ensures server-side prices reflect the current selection state
                // before the checkout screen fetches FastCartTotals.
                if (cartId) {
                    try {
                        await recalculateCartTotals({
                            variables: { input: { cart_id: cartId } },
                        });
                    } catch (recalcError) {
                        // Non-blocking: checkout can proceed even if recalculation fails
                        console.warn('[CartTotal] recalculateCartTotals failed (non-blocking):', recalcError);
                    }
                }

                const syncedReferences = references$().get() ?? [];
                const validCartItems = syncedReferences.filter(ref => ref.cartItem?.id);

                if (validCartItems.length === 0) {
                    showSnackbar({
                        message: t('text_cart_sync_no_items'),
                        variant: 'error',
                        duration: 5000,
                    });
                    return;
                }

                const selectedSyncedItems = validCartItems.filter(
                    ref => ref.isSelected && !isOutOfStock(ref.cartItem?.product?.sku ?? '')
                );

                if (selectedSyncedItems.length === 0) {
                    showSnackbar({
                        message: t('text_no_valid_items_for_checkout'),
                        variant: 'error',
                        duration: 5000,
                    });
                    return;
                }

                startTransition(() => {
                    router.push('/payment/checkout');
                });
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : t('text_unknown_error_occurred');

                showSnackbar({
                    message: t(errorMessage),
                    variant: 'error',
                    duration: 8000,
                });
                return;
            } finally {
                setIsLoading(false);
            }
        }, [
            requireAuthentication,
            showSnackbar,
            t,
            references$,
            isOutOfStock,
            selectedItemIds,
            router,
            cartId,
            recalculateCartTotals,
        ]);

        const removeInvalidBeforeForceSync = useCallback(async () => {
            removeInvalidSelectedLogics();
            await syncCartItemReferences();
        }, [removeInvalidSelectedLogics, syncCartItemReferences]);

        const handlePressCartButton = useCallback(async () => {
            const invalidLogics = getCartItemsHasInvalidLogics();

            if (invalidLogics?.length > 0) {
                handleShowDialog(
                    t(
                        'text_gifts_out_of_stock_message',
                        'Quà tặng của sản phẩm đã hết. Ba mẹ có muốn tiếp tục mua hàng?'
                    ),
                    removeInvalidBeforeForceSync,
                    handleCloseDialog
                );
                return;
            }
            await syncCartItemReferences();
        }, [
            syncCartItemReferences,
            removeInvalidBeforeForceSync,
            handleCloseDialog,
            getCartItemsHasInvalidLogics,
            handleShowDialog,
            t,
        ]);

        // Handle error display with Snackbar (only for sync errors now)
        useEffect(() => {
            if (syncedError?.message) {
                showSnackbar({
                    message: t(syncedError.message),
                    variant: 'error',
                    duration: 8000,
                });
            }
        }, [syncedError?.message, showSnackbar, t]);

        if (isPending) {
            return <LoadingOverlay showIf={isLoading} message={t('text_preparing_your_order_message')} />;
        }

        return (
            <>
                <CartTotalView
                    finalPrice={grandTotalPrice}
                    discountPrice={savingsAmount}
                    originalPrice={originalPrice}
                    onButtonClick={handlePressCartButton}
                    isAuthorized={isAuthenticated}
                    buyNowVisible={!!selectedItemIds?.length}
                    checked={isAllCartItemReferenceSelected}
                    onChecked={handleSelectAllCartItemReference}
                />
                <LoadingOverlay showIf={isLoading} message={t('text_preparing_your_order_message')} />
            </>
        );
    }
);

const CartTotalContent: FC<WithDialogProps> = props => {
    return <CartTotalInner {...props} />;
};

export const CartTotal = withDialog(CartTotalContent);
